// Remédios de hoje: as doses da prescrição, na ordem do que pede atenção.
//
// Pedido do Ilton (27/09): "clicar nos medicamentos: foi feito, foi feito" — e
// o que passou da hora sem ninguém confirmar aparece primeiro, como pergunta.
// Atrasado não quer dizer que não foi dado: quer dizer que ninguém disse que foi.
//
// A hora do "foi feito" fica à vista e se ajusta antes do toque: marcar às 7h30
// a dose das 6h não pode gravar 7h30 se foi dada às 6h.
import { useCallback, useEffect, useState } from 'react';
import {
  formatarData, formatarHora, podeEscrever,
  type AlertaDeRemedio, type AplicacaoDoDia, type DoseDoDia, type Perfil, type RemedioDoDia,
} from '@medicov1/shared';
import { chamar } from '../api';
import './remedios.css';

interface DiaDTO {
  dia: string;
  agora: string;
  minutosParaDesfazer: number;
  doses: DoseDoDia[];
  seNecessario: (RemedioDoDia & { hoje: AplicacaoDoDia[] })[];
  continuos: RemedioDoDia[];
  semHorario: RemedioDoDia[];
  alertas: AlertaDeRemedio[];
  resumo: { total: number; dadas: number; atrasadas: number; proxima: DoseDoDia | null };
}

const ORDEM: Record<DoseDoDia['situacao'], number> = { atrasado: 0, agora: 1, depois: 2, dado: 3 };
const ROTULO: Record<DoseDoDia['situacao'], string> = { atrasado: 'foi dado?', agora: 'está na hora', depois: 'mais tarde', dado: 'dado' };

/** "07:32" agora, no relógio de Brasília. */
const horaAgora = () => formatarHora(new Date());

interface Props { pacienteId: string; perfil: Perfil; usuarioId: string; aoVoltar: () => void }

export function RemediosDoDia({ pacienteId, perfil, usuarioId, aoVoltar }: Props) {
  const registra = podeEscrever(perfil);
  const [dados, setDados] = useState<DiaDTO | null>(null);
  const [horas, setHoras] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState('');
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      setDados(await chamar<DiaDTO>(`/medicacoes/dia?pacienteId=${pacienteId}`));
      setErro('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui buscar os remédios');
    }
  }, [pacienteId]);

  useEffect(() => {
    void carregar();
    const t = setInterval(() => void carregar(), 60_000);
    return () => clearInterval(t);
  }, [carregar]);

  const chave = (d: DoseDoDia) => `${d.medicacaoId}|${d.previstoEm}`;
  // Atrasado: o mais provável é ter sido dado na hora e ninguém marcou — a hora
  // proposta é a da prescrição. Na hora: agora.
  const horaProposta = (d: DoseDoDia) => horas[chave(d)] ?? (d.situacao === 'atrasado' ? d.hora : horaAgora());

  async function marcar(medicacaoId: string, hora: string, previstoEm?: string, rotuloOcupado = medicacaoId) {
    if (!dados) return;
    setOcupado(rotuloOcupado);
    setErro('');
    try {
      await chamar(`/medicacoes/${medicacaoId}/doses`, {
        metodo: 'POST',
        corpo: { ...(previstoEm ? { previstoEm } : {}), ocorreuEm: `${dados.dia}T${hora}:00-03:00` },
      });
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui marcar');
    } finally {
      setOcupado('');
    }
  }

  async function desfazer(id: string) {
    setOcupado(id);
    try {
      await chamar(`/administracoes/${id}`, { metodo: 'DELETE' });
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui desfazer');
    } finally {
      setOcupado('');
    }
  }

  /**
   * Resolver a sinalização: o aviso sai da tela, e o que o médico decidiu fica
   * anotado — por isso a pergunta, e sem resposta nada muda.
   */
  async function resolverAlerta(a: AlertaDeRemedio) {
    const decisao = window.prompt(`${a.nome}: o que o médico decidiu?\n(ex.: "pode dar" ou "suspenso até a cirurgia")`);
    if (!decisao || !decisao.trim()) return;
    setOcupado(`alerta|${a.medicacaoId}`);
    try {
      await chamar(`/medicacoes/${a.medicacaoId}/alerta`, { metodo: 'PUT', corpo: { alerta: null, resolucao: decisao.trim() } });
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui resolver o alerta');
    } finally {
      setOcupado('');
    }
  }

  const podeDesfazer = (a: AplicacaoDoDia | null) => Boolean(
    a && a.veredito === 'feito' && a.autorId === usuarioId && dados
      && Date.parse(dados.agora) - Date.parse(a.criadoEm) < dados.minutosParaDesfazer * 60_000,
  );

  const doses = [...(dados?.doses ?? [])].sort((a, b) => ORDEM[a.situacao] - ORDEM[b.situacao] || a.previstoEm.localeCompare(b.previstoEm));

  return (
    <main className="rd">
      <header className="rd__topo">
        <div>
          <h1>Remédios de hoje</h1>
          {dados && (
            <p>
              {formatarData(dados.dia)} · <b>{dados.resumo.dadas} de {dados.resumo.total}</b> dados
              {dados.resumo.atrasadas > 0 && <> · <b className="rd__alerta">{dados.resumo.atrasadas} sem confirmação</b></>}
            </p>
          )}
        </div>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      <p className="rd__dica">
        Da prescrição cadastrada. <b>Foi dado?</b> quer dizer que ninguém marcou ainda — não que não foi dado.
        Pergunte à enfermagem e marque com a hora em que foi.
      </p>
      {erro && <p className="aviso-erro">{erro}</p>}
      {!dados && !erro && <p className="rd__dica">carregando…</p>}

      {/* Sinalização urgente (28/09): antes de tudo, o que não pode passar batido. */}
      {dados && dados.alertas.length > 0 && (
        <section className="rd__urgente" role="alert">
          <h2>⚠ Sinalização urgente</h2>
          <ul>
            {dados.alertas.map((a) => (
              <li key={a.medicacaoId}>
                <b>{a.nome}</b>
                <p>{a.alerta}</p>
                {registra && (
                  <button onClick={() => void resolverAlerta(a)} disabled={ocupado === `alerta|${a.medicacaoId}`}>
                    Resolvido com o médico
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <ul className="rd__lista">
        {doses.map((d) => (
          <li key={chave(d)} className={`rd__dose rd__dose--${d.situacao}${d.alerta && !d.aplicacao ? ' rd__dose--alerta' : ''}`}>
            <span className="rd__hora">{d.hora}</span>
            <div className="rd__nome">
              <b>{d.nome}</b>
              <small>{[d.dose, d.via].filter(Boolean).join(' · ')}</small>
            </div>
            <span className={`rd__chip rd__chip--${d.situacao}`}>{ROTULO[d.situacao]}</span>
            {d.alerta && !d.aplicacao && <p className="rd__aviso">⚠ {d.alerta}</p>}
            {d.aplicacao ? (
              <div className="rd__feito">
                ✓ {formatarHora(d.aplicacao.ocorreuEm)} · {d.aplicacao.autor}
                {d.aplicacao.veredito !== 'feito' && ' · rótulo conferido'}
                {podeDesfazer(d.aplicacao) && (
                  <button onClick={() => void desfazer(d.aplicacao!.id)} disabled={ocupado === d.aplicacao.id}>desfazer</button>
                )}
              </div>
            ) : registra && d.situacao !== 'depois' ? (
              <div className="rd__marcar">
                <label>
                  <span>foi às</span>
                  <input type="time" value={horaProposta(d)} onChange={(e) => setHoras((h) => ({ ...h, [chave(d)]: e.target.value }))} />
                </label>
                <button onClick={() => void marcar(d.medicacaoId, horaProposta(d), d.previstoEm, chave(d))} disabled={ocupado === chave(d)}>
                  {ocupado === chave(d) ? 'Marcando…' : 'Foi feito'}
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {dados && dados.seNecessario.length > 0 && (
        <section className="rd__bloco">
          <h2>Se necessário</h2>
          <ul className="rd__lista">
            {dados.seNecessario.map((r) => (
              <li key={r.id} className="rd__dose rd__dose--sn">
                <div className="rd__nome">
                  <b>{r.nome}</b>
                  <small>{[r.dose, r.via].filter(Boolean).join(' · ')}</small>
                  {r.alerta && <p className="rd__aviso">⚠ {r.alerta}</p>}
                  {r.hoje.length > 0 && <small className="rd__hoje">hoje: {r.hoje.map((a) => `${formatarHora(a.ocorreuEm)} (${a.autor})`).join(', ')}</small>}
                </div>
                {registra && (
                  <div className="rd__marcar">
                    <label>
                      <span>dado às</span>
                      <input type="time" value={horas[r.id] ?? horaAgora()} onChange={(e) => setHoras((h) => ({ ...h, [r.id]: e.target.value }))} />
                    </label>
                    <button onClick={() => void marcar(r.id, horas[r.id] ?? horaAgora())} disabled={ocupado === r.id}>Foi dado</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {dados && dados.semHorario.length > 0 && (
        <section className="rd__bloco rd__bloco--pergunta">
          <h2>Horário a confirmar com a equipe</h2>
          <p className="rd__dica">Estão em uso, mas o horário não está cadastrado — por isso não entram na lista: {dados.semHorario.map((r) => r.nome).join(', ')}.</p>
        </section>
      )}

      {dados && dados.continuos.length > 0 && (
        <p className="rd__dica rd__continuos">Contínuos, sem marcar: {dados.continuos.map((r) => r.nome).join(', ')}.</p>
      )}
    </main>
  );
}
